import logger from '../../utils/logger';
import { catchError, switchMap } from 'rxjs/operators';
import { CreditStepKey } from '../../types/credit';
import { Epic, ofType } from 'redux-observable';
import { from, of } from 'rxjs';
import { getCompanyDetails, getStates } from '../../apis/profile';
import { getCreditApplicationInfo, handleComponentError } from '../../utils/credit';
import { mapCreditApplicationToFormData } from '../../utils/mapCreditApplicationToFormData';
import { setPageLoader } from '../../constants/loader';
import {
  fetchCreditData,
  prefillCreditApplicationData,
  setCreditApplicationError,
  updateStepperStatus,
} from '../slice/creditSlice';

const CREDIT_EPIC_LOADER_ID = 'credit-application-data-loader';

export const creditEpic: Epic = (action$, state$) =>
  action$.pipe(
    ofType(fetchCreditData),
    switchMap((action) => {
      const state = state$.value;
      const companyId = state.auth.authContext?.companyId;
      const currentEditStepKey: CreditStepKey | null = action.payload?.editingStepKey;

      if (!companyId) {
        setPageLoader(CREDIT_EPIC_LOADER_ID, false);
        return of(setCreditApplicationError('Company ID is missing'));
      }

      return from(
        Promise.all([getCreditApplicationInfo(), getCompanyDetails(companyId), getStates()]),
      ).pipe(
        switchMap(([creditApplicationInfo, companyDetails, states]) => {
          if (!companyDetails) {
            throw new Error('Failed to fetch company details');
          }

          const mappedData = mapCreditApplicationToFormData(
            creditApplicationInfo,
            companyDetails,
            states,
          );

          return from([
            updateStepperStatus({
              status: mappedData.creditApplicationStatus.status,
              currentEditStepKey: currentEditStepKey,
            }),
            prefillCreditApplicationData(mappedData),
          ]);
        }),
        catchError((error: unknown) => {
          let errorMessage = 'Failed to fetch credit application data';

          if (error && typeof error === 'object') {
            if (
              'response' in error &&
              error.response &&
              typeof error.response === 'object' &&
              'data' in error.response &&
              error.response.data &&
              typeof error.response.data === 'object' &&
              'message' in error.response.data &&
              typeof error.response.data.message === 'string'
            ) {
              errorMessage = error.response.data.message;
            } else if ('message' in error && typeof error.message === 'string') {
              errorMessage = error.message;
            }
          }

          logger('error', 'Error in fetchCreditDataEpic', error);
          setPageLoader(CREDIT_EPIC_LOADER_ID, false);
          handleComponentError(error);
          return of(setCreditApplicationError(errorMessage));
        }),
      );
    }),
  );
